<template>
  <div id="area-create">
    <v-overlay :value="LoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Premium Header Card with SpeechCraft Animation -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white"
                  >mdi-checkbox-multiple-marked</v-icon
                >
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">Create Criteria</h1>
                <p class="header-subtitle white--text mb-0">
                  Add a new evaluation criteria for your portfolio
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn icon class="action-btn" @click="closePrompt" title="Close">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Main Content Card -->
    <v-card elevation="12" class="form-card">
      <v-container class="pa-8">
        <v-row>
          <v-col cols="12" md="12">
            <p class="required-note">
              <v-icon small class="mr-1 text-danger">mdi-alert-circle</v-icon>
              <span class="text-danger font-weight-bold">*</span> indicates
              required field
            </p>
          </v-col>
        </v-row>
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="submitForm"
        >
          <v-container>
            <v-row wrap>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-text</v-icon>
                    <span class="text-danger">*</span> Criteria Name
                  </label>
                  <v-text-field
                    v-model="CriteriaName"
                    :rules="CriteriaNameRules"
                    placeholder="Enter criteria name"
                    required
                    outlined
                    dense
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-form-textbox</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-code</v-icon>
                    <span class="text-danger">*</span> Criteria Short Name
                  </label>
                  <v-text-field
                    v-model="CriteriaShortName"
                    :rules="CriteriaShortNameRules"
                    placeholder="Enter short name"
                    required
                    outlined
                    dense
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-identifier</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-sort-numeric</v-icon>
                    <span class="text-danger">*</span> Criteria Order
                  </label>
                  <v-text-field
                    v-model="CriteriaOrder"
                    :rules="CriteriaOrderRules"
                    placeholder="Enter order"
                    required
                    outlined
                    dense
                    type="number"
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-numeric</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-briefcase</v-icon>
                    <span class="text-danger">*</span> Portfolio
                  </label>
                  <v-autocomplete
                    :reduce="(option) => option.value"
                    v-model="PortfolioId"
                    :rules="PortfolioIdRules"
                    :items="PortfolioIdOptions"
                    :loading="PortfolioIdOptionsLoading"
                    dense
                    clearable
                    required
                    outlined
                    placeholder="Select portfolio"
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-file-document</v-icon>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-tag</v-icon>
                    <span class="text-danger">*</span> Event Category
                  </label>
                  <v-autocomplete
                    :reduce="(option) => option.value"
                    v-model="EventCategoryId"
                    :rules="EventCategoryIdRules"
                    :items="EventCategoryIdOptions"
                    :loading="EventCategoryIdOptionsLoading"
                    dense
                    clearable
                    required
                    outlined
                    placeholder="Select category"
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-format-list-bulleted</v-icon>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3" md="3">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-toggle-switch</v-icon>
                    <span class="text-danger">*</span> Status
                  </label>
                  <v-switch
                    inset
                    v-model="CriteriaStatus"
                    :label="`${CriteriaStatus ? 'Active' : 'Inactive'}`"
                    class="mt-2"
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
            <v-row wrap class="mt-4">
              <v-col cols="12" sm="6" lg="4" md="4">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-file-document</v-icon>
                    Description
                  </label>
                  <v-textarea
                    v-model="Description"
                    :rules="DescriptionRules"
                    placeholder="Enter criteria description"
                    outlined
                    dense
                    rows="3"
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-text-box-multiple</v-icon>
                    </template>
                  </v-textarea>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="4" md="4">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-cog-automatic</v-icon>
                    Is Automatic
                  </label>
                  <v-switch
                    inset
                    v-model="IsAutomatic"
                    :label="`${IsAutomatic ? 'Yes' : 'No'}`"
                    class="mt-2"
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="4" md="4" v-if="RoleId == 2">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-variable</v-icon>
                    Field Name
                  </label>
                  <v-text-field
                    v-model="FieldName"
                    :rules="FieldNameRules"
                    placeholder="Enter field name"
                    outlined
                    dense
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-identifier</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="4" md="4">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-toggle-switch</v-icon>
                    Is Completed
                  </label>
                  <v-switch
                    inset
                    v-model="IsCompleted"
                    :label="`${IsCompleted ? 'Yes' : 'No'}`"
                    class="mt-2"
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="4" md="4">
                <div class="form-field-wrapper">
                  <label class="form-label">
                    <v-icon small color="#2B9EB3">mdi-calendar-star</v-icon>
                    Sub Criteria Event Type
                  </label>
                  <v-autocomplete
                    v-model="EfficiencySubCriteriaEventType"
                    :items="EfficiencySubCriteriaEventTypeOptions"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    placeholder="Select event type"
                    class="custom-input"
                  >
                    <template v-slot:prepend>
                      <v-icon color="#2B9EB3">mdi-calendar-range</v-icon>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
            </v-row>
            <v-row wrap class="mt-4">
              <v-col align="center" cols="12" md="12">
                <v-btn
                  :disabled="!valid1"
                  @click.prevent="submitForm"
                  :loading="SubmitFlag"
                  elevation="8"
                  rounded
                  large
                  color="#1db954"
                  class="px-12 font-weight-bold white--text"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Submit
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    AddRecordPrompt: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      LoadingFlag: false,
      SubmitFlag: false,

      CriteriaName: "",
      CriteriaShortName: "",
      CriteriaOrder: 1,
      CriteriaStatus: 1,
      Description: "",
      IsAutomatic: 0,
      IsCompleted: 0,
      FieldName: "",
      RoleId: "",
      EfficiencySubCriteriaEventType: 0,
      EfficiencySubCriteriaEventTypeOptions: [
        { value: 0, text: "Default" },
        { value: 1, text: "Zone Event" },
        { value: 2, text: "National Event" },
      ],

      CriteriaNameRules: [(v) => !!v || "Criteria Name is required"],
      CriteriaShortNameRules: [],
      CriteriaOrderRules: [(v) => !!v || "Criteria Order is required"],
      DescriptionRules: [],
      IsAutomaticRules: [],
      FieldNameRules: [],

      PortfolioIdRules: [(v) => !!v || "Portfolio is required"],
      PortfolioId: "",
      PortfolioIdOptions: [],
      PortfolioIdOptionsLoading: false,

      EventCategoryIdRules: [(v) => !!v || "Event category is required"],
      EventCategoryId: "",
      EventCategoryIdOptions: [],
      EventCategoryIdOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      ResultFlag: false,
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;
        this.CurrentYearId = CurrentJciYearId;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        this.RoleId = RoleId;

        this.getPortfolioIdOptions();
      }
    },
    PortfolioId: function () {
      console.log("watch PortfolioId");
      this.EventCategoryId = "";
      this.getEventCategoryIdOptions();
    },
    PortfolioIdOptions: function () {
      console.log("watch PortfolioIdOptions");
      this.LoadingFlag = false;
      this.PortfolioIdOptionsLoading = false;
    },
    EventCategoryId: function () {
      console.log("watch EventCategoryId");
      this.EventSubTypeId = "";
      this.getEventSubTypeIdOptions();
    },
    EventCategoryIdOptions: function () {
      console.log("watch EventCategoryIdOptions");
      this.LoadingFlag = false;
      this.EventCategoryIdOptionsLoading = false;
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "full_efficiency",
        Action: "create_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideDialog", this.ResultFlag);
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        var upload = {
          UserInterface: 1,
          CriteriaName: this.CriteriaName,
          CriteriaShortName: this.CriteriaShortName,
          CriteriaOrder: this.CriteriaOrder,
          CriteriaStatus: this.CriteriaStatus,
          Portfolio: this.PortfolioId,
          JciEventType: this.EventCategoryId,
          Description: this.Description,
          IsAutomatic: this.IsAutomatic ? 1 : 0,
          IsCompleted: this.IsCompleted ? 1 : 0,
          FieldName: this.FieldName,
          EfficiencySubCriteriaEventType: this.EfficiencySubCriteriaEventType,
        };
        console.log({ upload });

        this.progessStart = 1;
        this.SubmitFlag = true;
        this.ResultFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/criteria/store";
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.progessStart = 0;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.ResultFlag = true;
              thisIns.closePrompt();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.progessStart = 0;
            thisIns.SubmitFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        this.sweetAlert("error", message, false);
      }
    },
    getPortfolioIdOptions() {
      console.log("getPortfolioIdOptions called");
      this.LoadingFlag = true;
      this.PortfolioIdOptionsLoading = true;
      var selectbox1_source = "PortfolioId";
      var selectbox1_destination = "PortfolioIdOptions";
      var selectbox1_url = "api/portfolio/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getEventCategoryIdOptions() {
      console.log("getEventCategoryIdOptions called");
      var PortfolioId = this.PortfolioId;
      console.log("PortfolioId=" + PortfolioId);
      if (PortfolioId != "") {
        this.LoadingFlag = true;
        this.EventCategoryIdOptionsLoading = true;
        var selectbox1_source = "EventCategoryId";
        var selectbox1_destination = "EventCategoryIdOptions";
        var selectbox1_url = "api/jci-event-type/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          Portfolio: PortfolioId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (PortfolioId == "") {
          message += "Portfolio should not be empty. ";
        }
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette (JCI Approved Colors)
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

#area-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // Header Card Styles with SpeechCraft Animation
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // Form Card Styles
  .form-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .required-note {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    border-left: 4px solid #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #7f1d1d;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .form-field-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-label {
    font-size: 14px;
    font-weight: 700;
    color: $text-primary;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .custom-input {
    &::v-deep {
      .v-input__prepend-inner {
        margin-right: 8px !important;
        color: $aqua !important;
      }

      .v-field--focused {
        color: $aqua;
      }
    }
  }

  @media (max-width: 960px) {
    .header-title {
      font-size: 24px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }

  @media (max-width: 600px) {
    padding: 16px 12px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 20px;
    }

    .form-card {
      border-radius: 12px !important;
    }
  }
}
</style>